<template>
  <div
    class="confirmation-state"
    :class="`confirmation-state--${type}`"
    role="alert"
    :aria-live="type === 'success' ? 'polite' : 'assertive'"
  >
    <!-- Success State -->
    <template v-if="type === 'success'">
      <i class="fas fa-check-circle confirmation-state__icon"></i>
      <h2 class="confirmation-state__heading">Order Confirmed</h2>
      <div class="confirmation-state__summary">
        <div class="confirmation-state__detail">
          <span class="confirmation-state__detail-label">Member</span>
          <span class="confirmation-state__detail-value">{{ orderSummary.memberName }}</span>
        </div>
        <div class="confirmation-state__detail">
          <span class="confirmation-state__detail-label">Items</span>
          <span class="confirmation-state__detail-value">{{ orderSummary.itemCount }}</span>
        </div>
        <div class="confirmation-state__detail">
          <span class="confirmation-state__detail-label">Total</span>
          <span class="confirmation-state__detail-value confirmation-state__detail-value--total">R{{ formattedTotal }}</span>
        </div>
        <div class="confirmation-state__detail">
          <span class="confirmation-state__detail-label">Payment</span>
          <span class="confirmation-state__detail-value">{{ formattedPaymentMethod }}</span>
        </div>
        <div v-if="orderSummary.branchName" class="confirmation-state__detail">
          <span class="confirmation-state__detail-label">Branch</span>
          <span class="confirmation-state__detail-value">{{ orderSummary.branchName }}</span>
        </div>
      </div>
      <b-button
        variant="outline-light"
        class="confirmation-state__action-btn mt-3"
        @click="dismiss"
      >
        New Transaction
      </b-button>
    </template>

    <!-- Error State -->
    <template v-else>
      <i class="fas fa-times-circle confirmation-state__icon"></i>
      <h2 class="confirmation-state__heading">Order Failed</h2>
      <p class="confirmation-state__message">{{ errorMessage }}</p>
      <div v-if="errorDetails && errorDetails.length" class="confirmation-state__error-details">
        <div
          v-for="detail in errorDetails"
          :key="detail.product_id"
          class="confirmation-state__error-line"
        >
          {{ detail.product_name || ('Product #' + detail.product_id) }}: requested {{ detail.requested }}, only {{ detail.available }} available
        </div>
      </div>
      <b-button
        variant="outline-light"
        class="confirmation-state__action-btn mt-3"
        @click="dismiss"
      >
        Try Again
      </b-button>
    </template>
  </div>
</template>

<script>
export default {
  name: "confirmationState",
  props: {
    type: {
      type: String,
      required: true,
      validator: (v) => ["success", "error"].includes(v),
    },
    orderSummary: {
      type: Object,
      default: () => ({ memberName: "", itemCount: 0, total: 0, paymentMethod: "cash", branchName: "" }),
    },
    errorMessage: {
      type: String,
      default: "Something went wrong",
    },
    errorDetails: {
      type: Array,
      default: () => [],
    },
    autoDismissMs: {
      type: Number,
      default: 3000,
    },
  },
  data() {
    return {
      dismissTimer: null,
    };
  },
  computed: {
    formattedTotal() {
      const total = parseFloat(this.orderSummary.total) || 0;
      return total.toFixed(2);
    },
    formattedPaymentMethod() {
      return this.orderSummary.paymentMethod === "card" ? "Card" : "Cash";
    },
  },
  mounted() {
    if (this.type === "success" && this.autoDismissMs > 0) {
      this.dismissTimer = setTimeout(() => {
        this.dismiss();
      }, this.autoDismissMs);
    }
  },
  beforeDestroy() {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  },
  methods: {
    dismiss() {
      if (this.dismissTimer) {
        clearTimeout(this.dismissTimer);
        this.dismissTimer = null;
      }
      this.$emit("dismissed");
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";
@import "~@/design/tokens/feedback";
@import "~@/design/tokens/surfaces";

.confirmation-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: $space-6;
  border-radius: 8px;
  min-height: 400px;
  color: #ffffff;

  &--success {
    background: $feedback-success;
  }

  &--error {
    background: $feedback-error;
  }

  &__icon {
    font-size: 48px;
    margin-bottom: $space-4;
  }

  &__heading {
    font-size: $type-h1;
    font-weight: 700;
    margin-bottom: $space-4;
  }

  &__summary {
    width: 100%;
    max-width: 280px;
  }

  &__detail {
    display: flex;
    justify-content: space-between;
    padding: $space-2 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  &__detail-label {
    font-size: $type-body;
    opacity: 0.85;
  }

  &__detail-value {
    font-size: $type-body;
    font-weight: 600;

    &--total {
      font-size: $type-h2;
      font-weight: 700;
    }
  }

  &__message {
    font-size: $type-body-lg;
    margin-bottom: $space-3;
    max-width: 320px;
  }

  &__error-details {
    width: 100%;
    max-width: 320px;
    text-align: left;
    margin-bottom: $space-3;
  }

  &__error-line {
    font-size: $type-small;
    padding: $space-1 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  &__action-btn {
    min-height: 48px;
    min-width: 180px;
    font-weight: 600;
    font-size: $type-body-lg;
  }
}
</style>
